/**
 * Solutions page marketing copy — single source of truth.
 *
 * Both the SPA (`SolutionsPage`) and the prerendered static twin
 * (`SolutionsPageStatic`) read from here, and the prerender script builds
 * JSON-LD from the FAQ array. Edit copy here, never in the page components.
 */

export const SOLUTIONS_LINKEDIN_URL = 'https://www.linkedin.com/in/kirill-samorodov/';
export const SOLUTIONS_CONTACT_EMAIL = 'kirill@relix.delivery';

/** Hero — the H1 itself lives in the components; supporting line is shared here. */
export const solutionsHero = {
  eyebrow: 'AI DELIVERY LAYER FOR JIRA · BETA',
  supporting:
    'Relix turns Jira signals into one delivery decision layer — so you ship faster, stay in control, and see risk early.',
  secondaryCta: 'See Delivery Review',
} as const;

/** Trust/proof block between integrations and the master video. */
export const solutionsTrust = {
  eyebrow: 'WHO RELIX IS FOR',
  heading: 'Built for the people who answer for the release.',
  sub: 'Product Owners, Heads of Product, and Delivery Leads use Relix to walk into every planning and review conversation with facts — not feelings.',
  badges: ['Built from real Product Owner workflows', 'Runs on your real Jira data'] as const,
} as const;

/** Integrations strip under the hero — Jira is required; Microsoft is capacity-only. */
export const solutionsIntegrations = {
  eyebrow: 'CONNECTS WITH YOUR DELIVERY STACK',
  cards: [
    {
      id: 'atlassian',
      title: 'Atlassian',
      subtitle: 'Jira issues, sprints, releases',
      badge: 'Required',
      badgeKind: 'required',
    },
    {
      id: 'microsoft',
      title: 'Microsoft',
      subtitle: 'Microsoft 365, SharePoint, calendars',
      badge: 'Optional — calendar & capacity',
      badgeKind: 'optional',
    },
  ],
} as const;

/** 3-step "how it works" block — sits next to the metro-map (signal layer). */
export const solutionsHowItWorks = {
  eyebrow: 'HOW IT WORKS',
  heading: 'From Jira to a delivery decision in three steps.',
  steps: [
    {
      num: '1',
      title: 'Connect Jira',
      body: 'Your own Atlassian API token — no admin approval, nothing to install. The first sync starts immediately.',
    },
    {
      num: '2',
      title: 'Decide what ships',
      body: 'A prioritized decision queue shows what to act on — before the release date moves.',
    },
    {
      num: '3',
      title: 'Get your score',
      body: 'Relix reads scope, capacity, quality, and delivery history into one health score for every release.',
    },
  ] as const,
} as const;

/**
 * Master overview video (65s). Chapters mirror master-50s-chapters.vtt and were
 * verified against the actual video frames — keep them in sync when the video changes.
 */
export const solutionsVideo = {
  eyebrow: 'PRODUCT OVERVIEW · 65 SECONDS',
  transcriptLabel: 'What the video covers',
  playLabel: 'Play the overview',
  durationLabel: '1:05',
  posterSrc: '/brand/solutions/master-50s-poster.jpg',
  chaptersVttSrc: '/brand/solutions/master-50s-chapters.vtt',
  chapters: [
    { time: '0:01', title: 'Delivery drifts quietly' },
    { time: '0:04', title: 'Delivery health score — one number for the release' },
    { time: '0:08', title: 'Scope, risk, capacity, quality — scored from Jira' },
    { time: '0:15', title: 'Release plan — scope and dates in one place' },
    { time: '0:22', title: 'Release prep in minutes, not a week of status calls' },
    { time: '0:35', title: 'Explain what changed before it slips' },
    { time: '0:45', title: 'The data was already in Jira' },
    { time: '0:50', title: 'Know where estimates hold and where they miss' },
    { time: '0:57', title: 'Delivery confidence. One number' },
    { time: '1:04', title: 'Know what will ship before it slips' },
  ] as const,
} as const;

/** Founder block — personal-brand trust. Full section after the master video. */
export const solutionsFounder = {
  eyebrow: 'THE PERSON BEHIND RELIX',
  heading: 'Built by an operator, not a dashboard vendor.',
  body: [
    'I am Kirill Samorodov, the founder of Relix. I spent years as a Product Lead in Jira-driven teams — 6 product teams and ~30 people, release cadence from 2 a year to 12. I was the one who had to answer for the date, with nothing but stale exports and a capacity spreadsheet that broke every month.',
    'Relix is what I needed in those meetings: it prepares the release, shows how credible the commitment still is, and explains what changed — from the Jira data you already have.',
    'The release picture used to take me a week of status packing. Now it takes an evening — from the Jira data that was already there.',
    'I onboard you myself.',
  ],
  primaryCta: 'Request a 30-min delivery review',
  linkedinCta: 'Verify me on LinkedIn →',
  photoAlt: 'Kirill Samorodov, founder of Relix',
} as const;

/** "Also included" strip under the feature explorer — secondary modules, honestly listed. */
export const solutionsAlsoIncluded = {
  label: 'Also included',
  items: [
    'Custom reports & branded PDF export',
    'Release calendar, sprint plan & roadmap',
    'Worklog sync',
    'Microsoft 365 calendar sync',
  ] as const,
} as const;

/** Security & data section (id="solutions-security" — footer links here). */
export const solutionsSecurity = {
  eyebrow: 'SECURITY & DATA',
  heading: 'Your Jira data stays under your control.',
  sub: 'You connect with your own token and stay in control — disconnect at any time, and synced data is deleted on request.',
  /**
   * Compact trust strip (Solutions page). `icon` keys map to lucide
   * components in SolutionsTrustSections. Detailed cards below stay on /help.
   */
  strip: [
    { icon: 'key', title: 'Your own token', caption: 'No admin approval needed' },
    { icon: 'ban', title: 'Read-only', caption: 'We never write to Jira' },
    { icon: 'lock', title: 'Encrypted at rest', caption: 'Managed Postgres storage' },
    { icon: 'trash', title: 'Deleted on request', caption: 'Disconnect anytime' },
  ] as const,
  items: [
    {
      title: 'Your token, your access',
      body: 'Relix connects with an Atlassian API token you create yourself. No Jira admin approval, no marketplace plugin to install.',
    },
    {
      title: 'Read-only by design',
      body: 'Relix reads issues, sprints, releases, and worklogs — and never writes anything back to your Jira.',
    },
    {
      title: 'Dedicated, encrypted storage',
      body: 'Synced data lives in a managed Postgres database, encrypted at rest. Disconnect Jira at any time — and ask us to delete synced data.',
    },
  ] as const,
  /** Small plain-language privacy note (id="solutions-privacy"). */
  privacyNote:
    'Privacy, in plain words: audit-form details are used only to contact you about the audit. We never sell data, and Jira data is synced only to compute your delivery picture.',
} as const;

/**
 * Objection handling — "why not just…". Versus format: `them` is the muted
 * status-quo line, `relix` is the violet punchline. Keep both to one short
 * sentence — the contrast is the hook.
 */
export const solutionsObjections = {
  eyebrow: 'FAIR QUESTIONS',
  heading: 'Why not just…',
  items: [
    {
      question: '…use Jira dashboards?',
      them: 'Raw data. Years of practice to read it.',
      relix: 'The craft built in — reality at one glance.',
    },
    {
      question: '…plan in Advanced Roadmaps?',
      them: 'Plans that assume capacity.',
      relix: 'The plan checked against real capacity.',
    },
    {
      question: '…export to Excel?',
      them: 'Snapshots stale the day you export them.',
      relix: 'Always in sync with Jira.',
    },
  ] as const,
} as const;

/**
 * FAQ — also rendered as JSON-LD FAQPage by the prerender script. Keep answers
 * truthful.
 *
 * `scope: 'both'` items render on the Solutions page (conversion-critical
 * questions) AND on /help; `scope: 'help'` items render only on /help.
 * JSON-LD must match visible content, so each page's markup uses its own
 * filtered set.
 */
export const solutionsFaq = {
  eyebrow: 'FAQ',
  heading: 'Questions, answered.',
  moreLabel: 'See all questions on the Help page →',
  items: [
    {
      id: 'jira_server',
      scope: 'help',
      question: 'Does Relix work with Jira Server or Data Center?',
      answer:
        'Relix is built for Jira Cloud. Data Center can expose a REST API with personal access tokens, so a connection is technically possible — if that is your setup, write to us and we will work it out together.',
    },
    {
      id: 'admin_rights',
      scope: 'both',
      question: 'Do I need Jira admin rights to connect?',
      answer:
        'No. Any Jira user can create a personal API token in their own Atlassian account (id.atlassian.com → Security → Create API token). It takes about a minute, needs no admin, and inherits only your own Jira permissions.',
    },
    {
      id: 'setup_time',
      scope: 'both',
      question: 'How long does setup take?',
      answer:
        'Mostly it depends on your pace: connect Jira with an API token, then tune Relix to the criteria you care about. Hints guide you at every step, so it is hard to get stuck — expect a few hours at most, not weeks.',
    },
    {
      id: 'no_estimates',
      scope: 'both',
      question: 'What if we don’t track estimates?',
      answer:
        'Relix still reads scope, status flow, capacity, and quality signals, so you get value from day one. Forecast precision improves further when estimates exist.',
    },
    {
      id: 'write_back',
      scope: 'both',
      question: 'Does Relix write back to my Jira?',
      answer:
        'No — Relix is strictly read-only. It never creates, edits, moves, or deletes anything in your Jira. Your data stays exactly as your team left it.',
    },
    {
      id: 'who_for',
      scope: 'help',
      question: 'Who is Relix for?',
      answer:
        'CPOs, PMOs, Heads of Product, Delivery Leads, Product Owners, and Project Managers — anyone who answers for the release and wants facts instead of status-meeting opinions.',
    },
    {
      id: 'disconnect_data',
      scope: 'help',
      question: 'What happens to my data if I disconnect?',
      answer:
        'You can disconnect Jira at any time — the token is yours and revoking it cuts the sync instantly. Ask us and we delete the synced data from our encrypted storage as well.',
    },
    {
      id: 'audit_includes',
      scope: 'both',
      question: 'What does the free delivery audit include?',
      answer:
        'A 30-minute call where we walk through your delivery picture together, 30 days of Relix free with personal setup help, and a day-one view of your delivery risks and what to fix first.',
    },
  ] as const,
} as const;

/** Help page (/help) framing — hero and contact block. Sections are shared with Solutions. */
export const helpContent = {
  eyebrow: 'HELP CENTER',
  heading: 'Answers, security, and how to reach us.',
  sub: 'Everything about connecting Jira, setup, your data, and working with Relix — in one place.',
  contactEyebrow: 'CONTACT',
  contactHeading: 'Prefer to talk to a human?',
  contactBody:
    'Write to us — we reply within one business day. For setup questions, mention your Jira setup (Cloud or Data Center) so we can help faster.',
  contactEmailLabel: SOLUTIONS_CONTACT_EMAIL,
  contactLinkedinLabel: 'Kirill Samorodov on LinkedIn →',
} as const;

/** Audit form section copy (deliverables, privacy line, LinkedIn alt). */
export const solutionsAudit = {
  heading: 'See your delivery reality before the next release slips.',
  sub: 'Get a Free Delivery Audit and see where scope drift, capacity risk, and quality issues are already visible in your Jira data.',
  deliverables: [
    '30-minute delivery review call',
    '30 days of Relix free — with personal setup help',
    'Day-one picture of your delivery risks and what to fix first',
  ] as const,
  privacyLine:
    'We use your details only to contact you about the audit — no newsletters, no sharing.',
  linkedinAlt: 'Prefer to talk first?',
  linkedinCta: 'Write me on LinkedIn →',
  replyLine:
    'I read every request myself and reply within one business day — then we book the 30 minutes.',
  submitLabel: 'Request the free audit',
} as const;

/** Marketing footer rendered at the bottom of the Solutions page (SPA + static twin). */
export const solutionsFooter = {
  tagline: 'AI Delivery Intelligence for Jira Teams',
  links: [
    { label: 'Product', href: '/solutions' },
    // Pricing hidden until the page is re-opened (mirrors the header nav).
    { label: 'Help', href: '/help' },
    { label: 'Security', href: '/help#security' },
    { label: 'Privacy', href: '/help#privacy' },
    { label: 'Contact', href: SOLUTIONS_LINKEDIN_URL },
  ] as const,
  copyright: `© ${new Date().getFullYear()} Relix`,
} as const;
